import { Modal, Stack, TextInput, Group, Button, Text } from '@mantine/core';
import { useState, useEffect } from 'react';

interface Props {
  opened: boolean;
  title: string;
  message: string;
  /** Texto del boton de confirmacion. Por defecto 'Eliminar'. */
  confirmLabel?: string;
  /** Color del boton de confirmacion (semantico). Por defecto 'red'. */
  confirmColor?: string;
  /** Si true, pide teclear el nombre del elemento para confirmar. */
  requireTyping?: boolean;
  /** Texto que el usuario debe teclear para habilitar el boton. */
  expectedTyping?: string;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Dialog generico de confirmacion destructiva (eliminar/baja logica).
 *
 * Si `requireTyping=true`, fuerza al usuario a teclear el valor de
 * `expectedTyping` para confirmar — patron estandar en GitHub, GitLab,
 * Stripe, etc. para acciones irreversibles.
 */
export function ConfirmDialog({
  opened,
  title,
  message,
  confirmLabel = 'Eliminar',
  confirmColor = 'red',
  requireTyping = false,
  expectedTyping,
  loading,
  onConfirm,
  onCancel,
}: Props) {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (opened) setTyped('');
  }, [opened]);

  const canConfirm = !requireTyping || (expectedTyping && typed === expectedTyping);

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={title}
      centered
      size="md"
      withCloseButton={!loading}
    >
      <Stack gap="md">
        <Text size="sm">{message}</Text>
        {requireTyping && expectedTyping && (
          <TextInput
            label={`Teclea "${expectedTyping}" para confirmar`}
            value={typed}
            onChange={(e) => setTyped(e.currentTarget.value)}
            placeholder={expectedTyping}
            disabled={loading}
          />
        )}
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button color={confirmColor} onClick={onConfirm} loading={loading} disabled={!canConfirm}>
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
